import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { runAutoDelete } from '../utils/autoDelete';
import { AUTH_CONFIG } from '../config/auth';
import './Settings.css';

const Settings = () => {
  const [settings, setSettings] = useState({
    // Auto-delete settings - fixed 30 days
    autoDeleteTodayTomorrowEnabled: false,
    autoDeleteTrendingEnabled: false,
    autoDeleteGreetingsEnabled: false,
    autoDeleteLeaderUploadsEnabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [autoDeleting, setAutoDeleting] = useState(false);
  const [autoDeleteResults, setAutoDeleteResults] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAccessConfirm, setShowAccessConfirm] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessPassword, setAccessPassword] = useState('');
  const [accessError, setAccessError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const docRef = doc(db, 'settings', 'autoDelete');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // Only load auto-delete settings - fixed 30 days
        setSettings({
          autoDeleteTodayTomorrowEnabled: data.autoDeleteTodayTomorrowEnabled || false,
          autoDeleteTrendingEnabled: data.autoDeleteTrendingEnabled || false,
          autoDeleteGreetingsEnabled: data.autoDeleteGreetingsEnabled || false,
          autoDeleteLeaderUploadsEnabled: data.autoDeleteLeaderUploadsEnabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching auto-delete settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await setDoc(doc(db, 'settings', 'autoDelete'), {
        ...settings,
        updatedAt: new Date()
      });
      setMessage('Auto-delete settings saved successfully!');
    } catch (error) {
      console.error('Error saving auto-delete settings:', error);
      setMessage('Error saving auto-delete settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoDelete = async () => {
    setAutoDeleting(true);
    setAutoDeleteResults(null);
    setMessage('');

    try {
      const results = await runAutoDelete(settings);
      setAutoDeleteResults(results);

      if (results.totalDeleted > 0) {
        setMessage(`Auto-delete completed! Deleted ${results.totalDeleted} items total.`);
      } else {
        setMessage('Auto-delete completed! No items were old enough to delete.');
      }
    } catch (error) {
      console.error('Error running auto-delete:', error);
      setMessage('Error running auto-delete. Please try again.');
    } finally {
      setAutoDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleAccessConfirm = () => {
    if (accessPassword === AUTH_CONFIG.ADMIN_PASSWORD) {
      setAccessGranted(true);
      setShowAccessConfirm(false);
      setAccessError('');
    } else {
      setAccessError('Incorrect password. Access denied.');
      setAccessPassword('');
    }
  };

  const handleAccessCancel = () => {
    // Redirect to dashboard or previous page
    window.history.back();
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner"></div>
        <p>Loading auto-delete settings...</p>
      </div>
    );
  }

  // Show access confirmation dialog first
  if (showAccessConfirm && !accessGranted) {
    return (
      <div className="settings">
        <div className="modal-overlay">
          <div className="modal access-confirm-modal">
            <div className="modal-header">
              <h3>🔒 Admin Access Required</h3>
              <button
                className="back-btn"
                onClick={handleAccessCancel}
                title="Go back"
              >
                ← Back
              </button>
            </div>
            <div className="modal-body">
              <p><strong>⚠️ Warning: Restricted Area</strong></p>
              <p>You are about to access the Auto-Delete Settings page.</p>
              <p>This area contains powerful tools that can permanently delete data from the system.</p>
              <p>Please enter the admin password to continue:</p>

              <div className="form-group">
                <label htmlFor="accessPassword">Admin Password</label>
                <input
                  type="password"
                  id="accessPassword"
                  value={accessPassword}
                  onChange={(e) => setAccessPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAccessConfirm()}
                  placeholder="Enter admin password"
                  autoFocus
                />
                {accessError && (
                  <small className="error-text">{accessError}</small>
                )}
              </div>

              <div className="warning-text">
                <p>⚠️ Only authorized administrators should access this page.</p>
                <p>Unauthorized access is strictly prohibited.</p>
              </div>
            </div>
            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={handleAccessCancel}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAccessConfirm}
                disabled={!accessPassword.trim()}
              >
                Confirm Access
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="page-header settings-header">
        <h2>⚙️ Settings</h2>
        <div className="access-indicator">
          <span className="access-badge">🔒 Admin Access Granted</span>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-section">
          <h3>Configure Auto-Delete</h3>
          <p className="section-description">
            Configure automatic deletion of old uploads to manage storage space.
            Items older than 30 days will be permanently deleted.
          </p>

          {/* Today+Tomorrow Auto-Delete */}
          <div className="auto-delete-group">
            <h4>Today+Tomorrow Uploads</h4>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="autoDeleteTodayTomorrowEnabled"
                  checked={settings.autoDeleteTodayTomorrowEnabled}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Enable auto-delete for Today+Tomorrow uploads (30 days)
              </label>
            </div>
          </div>

          {/* Trending Auto-Delete */}
          <div className="auto-delete-group">
            <h4>Trending Uploads</h4>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="autoDeleteTrendingEnabled"
                  checked={settings.autoDeleteTrendingEnabled}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Enable auto-delete for Trending uploads (30 days)
              </label>
            </div>
          </div>

          {/* Greetings Auto-Delete */}
          <div className="auto-delete-group">
            <h4>Greetings Uploads</h4>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="autoDeleteGreetingsEnabled"
                  checked={settings.autoDeleteGreetingsEnabled}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Enable auto-delete for Greetings uploads (30 days)
              </label>
            </div>
          </div>

          {/* Leader Uploads Auto-Delete */}
          <div className="auto-delete-group">
            <h4>Leader Uploads</h4>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="autoDeleteLeaderUploadsEnabled"
                  checked={settings.autoDeleteLeaderUploadsEnabled}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Enable auto-delete for Leader uploads (30 days)
              </label>
            </div>
          </div>

          {/* Manual Auto-Delete Trigger */}
          <div className="auto-delete-actions">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={autoDeleting || (!settings.autoDeleteTodayTomorrowEnabled && !settings.autoDeleteTrendingEnabled && !settings.autoDeleteGreetingsEnabled && !settings.autoDeleteLeaderUploadsEnabled)}
            >
              {autoDeleting ? 'Running Auto-Delete...' : 'Run Auto-Delete Now'}
            </button>
            <small>Manually trigger auto-delete based on current settings</small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Auto-Delete Results */}
      {autoDeleteResults && (
        <div className="auto-delete-results">
          <h3>Auto-Delete Results</h3>
          <div className="results-summary">
            <p><strong>Total Items Deleted:</strong> {autoDeleteResults.totalDeleted}</p>
            {autoDeleteResults.totalErrors > 0 && (
              <p className="error"><strong>Errors:</strong> {autoDeleteResults.totalErrors}</p>
            )}
          </div>

          <div className="results-details">
            {autoDeleteResults.todayTomorrow.deletedSubcategories > 0 || autoDeleteResults.todayTomorrow.deletedUploads > 0 ? (
              <div className="result-item">
                <h4>Today+Tomorrow</h4>
                <p>Subcategories deleted: {autoDeleteResults.todayTomorrow.deletedSubcategories}</p>
                <p>Uploads deleted: {autoDeleteResults.todayTomorrow.deletedUploads}</p>
              </div>
            ) : null}

            {autoDeleteResults.trending.deletedSubcategories > 0 || autoDeleteResults.trending.deletedUploads > 0 ? (
              <div className="result-item">
                <h4>Trending</h4>
                <p>Subcategories deleted: {autoDeleteResults.trending.deletedSubcategories}</p>
                <p>Uploads deleted: {autoDeleteResults.trending.deletedUploads}</p>
              </div>
            ) : null}

            {autoDeleteResults.greetings.deletedSubcategories > 0 || autoDeleteResults.greetings.deletedUploads > 0 ? (
              <div className="result-item">
                <h4>Greetings</h4>
                <p>Subcategories deleted: {autoDeleteResults.greetings.deletedSubcategories}</p>
                <p>Uploads deleted: {autoDeleteResults.greetings.deletedUploads}</p>
              </div>
            ) : null}

            {autoDeleteResults.leaderUploads.deletedSubcategories > 0 || autoDeleteResults.leaderUploads.deletedUploads > 0 ? (
              <div className="result-item">
                <h4>Leader Uploads</h4>
                <p>Subcategories deleted: {autoDeleteResults.leaderUploads.deletedSubcategories}</p>
                <p>Uploads deleted: {autoDeleteResults.leaderUploads.deletedUploads}</p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal delete-confirm-modal">
            <div className="modal-header">
              <h3>⚠️ Confirm Auto-Delete</h3>
              <button
                className="close-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={autoDeleting}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p><strong>This action will permanently delete old uploads and subcategories!</strong></p>
              <p>The following collections will be processed:</p>
              <ul>
                {settings.autoDeleteTodayTomorrowEnabled && (
                  <li>Today+Tomorrow uploads older than 30 days</li>
                )}
                {settings.autoDeleteTrendingEnabled && (
                  <li>Trending uploads older than 30 days</li>
                )}
                {settings.autoDeleteGreetingsEnabled && (
                  <li>Greetings uploads older than 30 days</li>
                )}
                {settings.autoDeleteLeaderUploadsEnabled && (
                  <li>Leader uploads older than 30 days</li>
                )}
              </ul>
              <p className="warning-text">This action cannot be undone!</p>
            </div>
            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={autoDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleAutoDelete}
                disabled={autoDeleting}
              >
                {autoDeleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
